import { useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Alert } from "flowbite-react";
import { BiError } from "react-icons/bi";
import { Trophy, Eye, User, ArrowRight, Upload } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { updateUserOnboarding } from "services/auth.service";
import { getUserById, updateUserById } from "services/profile.service";
import { AuthContext } from "context/AuthContext";

import SpideyImg from "assets/img/Spidey.png";
import SteveImg from "assets/img/Steve.png";
import SuperguyImg from "assets/img/Superguy.png";
import TonyImg from "assets/img/Tony.png";
import WandaImg from "assets/img/Wanda.png";
import WolvyImg from "assets/img/Wolvy.png";

import "./styles.scss";

const avatars = [
  { name: "Spidey", image: SpideyImg },
  { name: "Steve", image: SteveImg },
  { name: "Superguy", image: SuperguyImg },
  { name: "Tony", image: TonyImg },
  { name: "Wanda", image: WandaImg },
  { name: "Wolvy", image: WolvyImg },
];

export function Onboarding() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedAvatar, setSelectedAvatar] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);

  const handleNext = async () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    } else {
      // Última etapa - salvar avatar e finalizar onboarding
      if (!selectedAvatar && !uploadedImage) {
        setErrorMessage("Selecione um avatar ou faça upload de uma foto para continuar");
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");

        if (!user?._id) {
          throw new Error("Usuário não autenticado");
        }

        // Finaliza o onboarding independente de avatar ou upload de foto
        // Se tiver avatar selecionado, salva o avatar E marca onboarding como false
        // Se tiver foto uploaded, apenas marca onboarding como false (foto já foi salva no upload)
        const avatarToSave = selectedAvatar || "custom"; // "custom" indica que usou foto própria
        await updateUserOnboarding(avatarToSave, user._id);

        // Toast de sucesso
        toast.success('Onboarding finalizado com sucesso! Redirecionando...', {
          position: 'top-right',
          autoClose: 1500,
        });

        // Aguarda um momento e então recarrega a página na home
        // O reload força o AuthContext a buscar os dados atualizados do usuário
        setTimeout(() => {
          window.location.href = "/";
        }, 1500);
      } catch (error: any) {
        setLoading(false);
        setErrorMessage(error.message || "Erro ao finalizar onboarding");
      }
    }
  };

  const handleSkip = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione um arquivo de imagem válido', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('O arquivo deve ter no máximo 5MB', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (!user?._id) {
      toast.error('Usuário não autenticado', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoadingUpload(true);

      // Convert File to base64 data URL
      const base64Image = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(event.target?.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Get current profile to preserve other fields
      const currentProfile = await getUserById(user._id);

      // Update profile with base64 image embedded in JSON
      const payload = {
        ...currentProfile,
        _id: user._id,
        image: {
          small: {
            url: base64Image
          }
        }
      };

      await updateUserById(user._id, payload);

      // Clear selected avatar when uploading custom image
      setSelectedAvatar("");
      setUploadedImage(base64Image);

      toast.success('Foto de perfil enviada com sucesso! Clique em "Próximo" para finalizar.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error: any) {
      console.error('Erro ao fazer upload do avatar:', error);
      const errorMessage = error?.response?.data?.message || 'Não foi possível fazer upload da foto. Tente novamente.';
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoadingUpload(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="onboarding-content">
            <div className="onboarding-icon">
              <Trophy size={64} strokeWidth={1.5} />
            </div>
            <h1 className="onboarding-title">Bem-vindo ao Rankifier!</h1>
            <p className="onboarding-description">
              Acompanhe sua posição e compare com outros jogadores.
            </p>
          </div>
        );
      case 1:
        return (
          <div className="onboarding-content">
            <div className="onboarding-icon">
              <Eye size={64} strokeWidth={1.5} />
            </div>
            <h1 className="onboarding-title">Veja sua Posição</h1>
            <p className="onboarding-description">
              Visualize sua posição no ranking em tempo real e compare seu
              desempenho com outros participantes.
            </p>
          </div>
        );
      case 2:
        return (
          <div className="onboarding-content">
            <div className="onboarding-icon">
              <User size={64} strokeWidth={1.5} />
            </div>
            <h1 className="onboarding-title">Personalize seu Avatar</h1>
            <p className="onboarding-description">
              Escolha um avatar que represente você no ranking.
            </p>

            <div className="avatar-grid">
              {avatars.map((avatar) => (
                <div
                  key={avatar.name}
                  className={`avatar-item ${
                    selectedAvatar === avatar.name ? "selected" : ""
                  } ${uploadedImage ? "disabled" : ""}`}
                  onClick={() => {
                    if (!uploadedImage) {
                      setSelectedAvatar(avatar.name);
                      setUploadedImage(null);
                    }
                  }}
                >
                  <img src={avatar.image} alt={avatar.name} />
                  {selectedAvatar === avatar.name && !uploadedImage && (
                    <div className="avatar-check">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="#10b981"/>
                        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Show uploaded image preview */}
            {uploadedImage && (
              <div className="uploaded-preview-inline">
                <img src={uploadedImage} alt="Preview" />
                <div className="avatar-check">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="#10b981"/>
                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Buttons row */}
            <div className="onboarding-buttons-row">
              <button 
                className="upload-photo-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={loadingUpload}
              >
                <Upload size={18} />
                <span>{loadingUpload ? 'Enviando...' : 'Upload'}</span>
              </button>

              <button
                onClick={handleNext}
                disabled={loading || loadingUpload}
                className="next-button"
              >
                <span>
                  {loading ? "Aguarde..." : "Próximo"}
                </span>
                {!loading && <ArrowRight size={18} />}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Bem-vindo - Rankifier</title>
      </Helmet>
      <div
        id="onboarding-wrapper"
        className="w-full min-h-screen flex items-center justify-center"
      >
        {/* Hidden file input for avatar upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleAvatarUpload}
        />

        <div className="onboarding-card">
          {renderStep()}

          {!!errorMessage && (
            <Alert color="failure" icon={BiError} className="mt-4">
              {errorMessage}
            </Alert>
          )}

          {/* Botão próximo para steps 0 e 1 */}
          {currentStep < 2 && (
            <div className="onboarding-actions">
              <Button
                onClick={handleNext}
                disabled={loading}
                className="onboarding-button-primary"
              >
                <span>
                  {loading ? "Aguarde..." : "Próximo"}
                </span>
                {!loading && <ArrowRight size={20} />}
              </Button>
            </div>
          )}

          <div className="onboarding-dots">
            {[0, 1, 2].map((step) => (
              <span
                key={step}
                className={`dot ${currentStep === step ? "active" : ""} ${
                  currentStep > step ? "completed" : ""
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
